import { useState, useMemo } from 'react';
import { EnhancedTableColumn } from '../../types';

/**
 * Hook for managing table column visibility state
 *
 * This hook provides functionality to show/hide table columns dynamically,
 * with support for initial state and change callbacks.
 *
 * @template T - The type of data rows in the table
 * @param columns - Array of table column definitions
 * @param initialState - Optional initial visibility state for columns
 * @param onColumnVisibilityChange - Optional callback when column visibility changes
 *
 * @returns Object containing:
 *   - visibility: Record of column visibility states
 *   - onColumnVisibilityChange: Function to toggle column visibility
 *   - visibleColumns: Filtered array of visible columns
 *
 * @example
 * ```tsx
 * const { visibility, onColumnVisibilityChange, visibleColumns } = useColumnVisibility(
 *   columns,
 *   { name: true, email: false },
 *   (columnId, visible) => console.log(`Column ${columnId} is now ${visible ? 'visible' : 'hidden'}`)
 * );
 * ```
 */
export const useColumnVisibility = <T>(
  columns: EnhancedTableColumn<T>[],
  initialState?: Record<string, boolean>,
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void,
) => {
  const [visibility, setVisibility] = useState<Record<string, boolean>>(
    initialState ??
      columns.reduce(
        (acc, col) => ({ ...acc, [String(col.id)]: !col.hidden }),
        {},
      ),
  );

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    setVisibility((prev) => ({ ...prev, [columnId]: visible }));
    onColumnVisibilityChange?.(columnId, visible);
  };

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibility[String(col.id)]),
    [columns, visibility],
  );

  return {
    visibility,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    visibleColumns,
  };
};
